import { Grid, LoadingOverlay, Modal, Space, TextInput } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { useModal } from '../../../hooks/useModal';
import { mutate } from '../../../util/api/mutate';
import { useServerGroupValidation } from '../hooks/useServerGroupValidation';
import { useUpsertGroupForm } from '../hooks/useUpsertGroupForm';
import type { GroupDto } from '../types/GroupDto';

interface EditGroupModal {
	close: () => void;
	isOpened: boolean;
	triggerListUpdate: () => void;
	initialGroup: GroupDto;
}

export function EditGroupModal({
	isOpened,
	close,
	triggerListUpdate,
	initialGroup,
}: EditGroupModal) {
	const { organisation } = useParams();

	const successModal = useModal();

	const { form, inputs } = useUpsertGroupForm(initialGroup);

	const editGroupMutation = mutate<GroupDto>(
		`${organisation}/groups/edit`,
		inputs,
	);

	useServerGroupValidation(editGroupMutation, form);

	const resetAndClose = () => {
		form.reset();
		close();
		successModal.close();
	};

	const updateAndClose = () => {
		resetAndClose();
		triggerListUpdate();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'Edit Group'} />}
				w={100}
				withCloseButton={true}
				size={'md'}
				centered
			>
				<LoadingOverlay visible={editGroupMutation.isLoading} />
				<SmallModal
					isOpen={successModal.isOpen}
					close={updateAndClose}
					title={'Group Edited!'}
					body={'A group has been successfully edited. Click Ok to continue...'}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await editGroupMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								successModal.open();
							}
						});
					})}
				>
					<Grid>
						<Grid.Col>
							<TextInput
								label={'Group Name'}
								placeholder={'Insert name of new group...'}
								key={form.key('groupName')}
								{...form.getInputProps('groupName')}
							/>
						</Grid.Col>
					</Grid>
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
